/**
 * ProfileListCard — one card for a contact's medications OR diagnoses
 * (Expansion Phase G). Rows: name (+ dose/frequency inline, muted), status
 * select, source chip (Form/Recruiter/AI/Lead), verify control (ghost
 * "Verify" → green check "Verified · {recruiter} · {relTime}"), and a two-tap
 * delete. Inline add + edit forms; saves are optimistic-or-spinner, never a
 * page spinner. Editing name or status clears Verified server-side — rows are
 * replaced with the server response so that's always reflected.
 */
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Check, Loader2, Pencil, Pill, Plus, Sparkles, Stethoscope, Trash2, X } from 'lucide-react';
import api from '../../../services/api';
import { ProfileItem } from '../../types';
import { relTime, useTeam } from '../../lib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const SOURCE_LABELS: Record<string, string> = {
  form: 'Form', recruiter: 'Recruiter', ai_extraction: 'AI', lead: 'Lead',
};

/** AI-extracted rows are unconfirmed until a recruiter verifies — show a distinct
 *  primary chip, a confidence tag, and the verbatim source line (lineage). */
export function SourceChips({ item }: { item: Pick<ProfileItem, 'source' | 'confidence'> }) {
  if (item.source === 'ai_extraction') {
    return (
      <>
        <span
          className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
          title="Added by the nightly AI from this lead's conversations — unconfirmed until you Verify"
        >
          <Sparkles className="h-3 w-3" /> AI
        </span>
        {item.confidence && (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
            {item.confidence} confidence
          </span>
        )}
      </>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
      title={item.source === 'form' ? "Recorded from a form/questionnaire the patient filled out — unconfirmed until you Verify" : undefined}
    >
      {SOURCE_LABELS[item.source] || item.source}
    </span>
  );
}

const STATUSES: Record<'medications' | 'conditions', Array<{ value: string; label: string }>> = {
  medications: [
    { value: 'active', label: 'Active' },
    { value: 'stopped', label: 'Stopped' },
    { value: 'unknown', label: 'Unknown' },
  ],
  conditions: [
    { value: 'active', label: 'Active' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'unknown', label: 'Unknown' },
  ],
};

interface Props {
  kind: 'medications' | 'conditions';
  contactId: string;
  reloadToken: number;
}

export default function ProfileListCard({ kind, contactId, reloadToken }: Props) {
  const { byId } = useTeam();
  const isMeds = kind === 'medications';
  const title = isMeds ? 'Medications' : 'Diagnoses';
  const singular = isMeds ? 'medication' : 'diagnosis';

  const [items, setItems] = useState<ProfileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [armedDelete, setArmedDelete] = useState<string | null>(null);

  // Inline add form
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [dose, setDose] = useState('');
  const [frequency, setFrequency] = useState('');
  const [dedupeMsg, setDedupeMsg] = useState<string | null>(null);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDose, setEditDose] = useState('');
  const [editFrequency, setEditFrequency] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/${kind}`);
      setItems(res.data?.items || []);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId, kind]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const n = new Set(prev);
      if (busy) n.add(id); else n.delete(id);
      return n;
    });

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    setActionError(null);
    setDedupeMsg(null);
    try {
      const body: Record<string, string> = { name: name.trim() };
      if (isMeds) {
        if (dose.trim()) body.dose = dose.trim();
        if (frequency.trim()) body.frequency = frequency.trim();
      }
      const res = await api.post(`/api/crm/contacts/${contactId}/${kind}`, body);
      const item: ProfileItem | undefined = res.data?.item;
      if (item) {
        if (res.data?.deduped) {
          setDedupeMsg(`"${item.name}" is already on file.`);
        } else {
          setItems((prev) => [...prev, item]);
          setName(''); setDose(''); setFrequency('');
        }
      }
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const patchItem = async (item: ProfileItem, patch: Record<string, unknown>) => {
    if (busyIds.has(item.id)) return;
    setActionError(null);
    setBusy(item.id, true);
    setItems((p) => p.map((i) => (i.id === item.id ? { ...i, ...patch } : i)));
    try {
      const res = await api.patch(`/api/crm/${kind}/${item.id}`, patch);
      const updated: ProfileItem | undefined = res.data?.item;
      if (updated) setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
      return true;
    } catch (e: any) {
      // Revert ONLY this row — a whole-array snapshot would clobber other
      // rows' concurrently confirmed updates.
      setItems((p) => p.map((i) => (i.id === item.id ? item : i)));
      setActionError(errMsg(e));
      return false;
    } finally {
      setBusy(item.id, false);
    }
  };

  const verify = async (item: ProfileItem) => {
    if (busyIds.has(item.id)) return;
    setActionError(null);
    setBusy(item.id, true);
    try {
      const res = await api.post(`/api/crm/${kind}/${item.id}/verify`);
      const updated: ProfileItem | undefined = res.data?.item;
      if (updated) setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setBusy(item.id, false);
    }
  };

  /** Two-tap delete: first tap arms (3s), second tap deletes. */
  const remove = async (item: ProfileItem) => {
    if (armedDelete !== item.id) {
      setArmedDelete(item.id);
      window.setTimeout(() => setArmedDelete((a) => (a === item.id ? null : a)), 3000);
      return;
    }
    setArmedDelete(null);
    setActionError(null);
    setBusy(item.id, true);
    setItems((p) => p.filter((i) => i.id !== item.id));
    try {
      await api.delete(`/api/crm/${kind}/${item.id}`);
    } catch (e: any) {
      // Re-insert only the failed row (created_at order), never a snapshot.
      setItems((p) => [...p, item].sort((a, b) => a.created_at.localeCompare(b.created_at)));
      setActionError(errMsg(e));
    } finally {
      setBusy(item.id, false);
    }
  };

  const startEdit = (item: ProfileItem) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditDose(item.dose || '');
    setEditFrequency(item.frequency || '');
  };

  const saveEdit = async (item: ProfileItem) => {
    if (!editName.trim()) return;
    const patch: Record<string, unknown> = { name: editName.trim() };
    if (isMeds) {
      patch.dose = editDose.trim() || null;
      patch.frequency = editFrequency.trim() || null;
    }
    const ok = await patchItem(item, patch);
    if (ok) setEditingId(null);
  };

  const inputCls =
    'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 min-h-[44px] sm:min-h-0';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          {isMeds
            ? <Pill className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            : <Stethoscope className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
          {title}
          {items.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({items.length})</span>
          )}
        </h3>
        <button
          type="button"
          onClick={() => { setFormOpen((o) => !o); setDedupeMsg(null); }}
          aria-label={formOpen ? 'Close the add form' : `Add a ${singular}`}
          className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
        >
          {formOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {formOpen ? 'Close' : 'Add'}
        </button>
      </div>

      {formOpen && (
        <form onSubmit={create} className="mb-3 space-y-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={isMeds ? 'Medication name (e.g. metformin)' : 'Diagnosis (e.g. type 2 diabetes)'}
            maxLength={200}
            className={inputCls}
          />
          {isMeds && (
            <div className="grid grid-cols-2 gap-2">
              <input value={dose} onChange={(e) => setDose(e.target.value)} placeholder="Dose (optional)" maxLength={200} className={inputCls} />
              <input value={frequency} onChange={(e) => setFrequency(e.target.value)} placeholder="Frequency (optional)" maxLength={200} className={inputCls} />
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            {dedupeMsg ? <p className="text-xs text-amber-600 dark:text-amber-400">{dedupeMsg}</p> : <span />}
            <button
              type="submit"
              disabled={!name.trim() || saving}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add
            </button>
          </div>
        </form>
      )}

      {actionError && (
        <p role="alert" className="mb-2 text-xs text-red-600 dark:text-red-400">{actionError}</p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}{' '}
          <button onClick={() => { setLoading(true); load(); }} className="font-medium underline">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {isMeds ? 'No medications recorded — add what they mention on the call.' : 'No diagnoses recorded — add what they mention on the call.'}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {items.map((item) => {
            const busy = busyIds.has(item.id);
            const verifier = item.verified_by ? byId.get(item.verified_by)?.name : null;
            return (
              <li key={item.id} className="py-2.5">
                {editingId === item.id ? (
                  <div className="space-y-2">
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      aria-label={`${singular} name`}
                      maxLength={200}
                      className={inputCls}
                    />
                    {isMeds && (
                      <div className="grid grid-cols-2 gap-2">
                        <input value={editDose} onChange={(e) => setEditDose(e.target.value)} placeholder="Dose" maxLength={200} className={inputCls} />
                        <input value={editFrequency} onChange={(e) => setEditFrequency(e.target.value)} placeholder="Frequency" maxLength={200} className={inputCls} />
                      </div>
                    )}
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="min-h-[44px] rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdit(item)}
                        disabled={!editName.trim() || busy}
                        className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
                      >
                        {busy && <Loader2 className="h-3 w-3 animate-spin" />} Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</span>
                        {isMeds && (item.dose || item.frequency) && (
                          <span className="ml-1.5 text-xs text-gray-400">
                            {[item.dose, item.frequency].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          aria-label={`Edit ${item.name}`}
                          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:min-h-0 sm:min-w-0"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(item)}
                          disabled={busy}
                          aria-label={armedDelete === item.id ? `Confirm delete ${item.name}` : `Delete ${item.name}`}
                          title={armedDelete === item.id ? 'Click again to confirm' : 'Delete'}
                          className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1 transition-colors duration-150 sm:min-h-0 sm:min-w-0 ${
                            armedDelete === item.id
                              ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <select
                        value={item.status}
                        onChange={(e) => patchItem(item, { status: e.target.value })}
                        disabled={busy}
                        aria-label={`${item.name} status`}
                        className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 min-h-[44px] sm:min-h-0"
                      >
                        {STATUSES[kind].map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      <SourceChips item={item} />
                      {item.verified_at ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                          <Check className="h-3.5 w-3.5" />
                          Verified{verifier ? ` · ${verifier}` : ''} · {relTime(item.verified_at)}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => verify(item)}
                          disabled={busy}
                          title="Mark this as confirmed with the patient"
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-500 transition-colors duration-150 hover:border-green-400 hover:text-green-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:text-green-400 sm:min-h-0"
                        >
                          Verify
                        </button>
                      )}
                    </div>
                    {item.source === 'ai_extraction' && item.source_excerpt && (
                      <p className="mt-1 border-l-2 border-primary-200 pl-2 text-xs italic text-gray-500 dark:border-primary-800 dark:text-gray-400">
                        “{item.source_excerpt}”
                      </p>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
